import React, { useMemo, useRef, useState, useCallback } from 'react';
import {
  Play,
  Pause,
  SkipForward,
  SkipBack,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  Repeat,
  ListMusic,
  Mic2,
  X,
  Settings2,
  Moon
} from 'lucide-react';
import { useSleepTimer, formatSleepTime } from '../hooks/useSleepTimer';
import { PlayerMobileNav, PlayerSidebar } from './listen/PlayerNav';
import { useListenSession } from '../hooks/useListenSession';
import ListenSettingsForm from './listen/ListenSettingsForm';
import LiveLyricsPanel from './listen/LiveLyricsPanel';
import ListenVerseStage from './listen/ListenVerseStage';
import GitaLogo from './GitaLogo';
import SlokaArtImage from './SlokaArtImage';
import { formatTime } from '../utils/formatTime';
import { languageLabel } from '../utils/defaultLanguage';
import { PLAYBACK_MODES } from '../utils/buildPlaybackQueue';

import { MOBILE_PLAYER_QUERY, useMediaQuery } from '../hooks/useMediaQuery';

const ListenStream = () => {
  const {
    voice,
    setVoice,
    localSettings,
    updateLocal,
    handleStart,
    hasQueue,
    currentTrack,
    isPlaying,
    isLoading,
    error,
    stopPlayback,
    togglePlay,
    playNext,
    playPrevious,
    playNextChapter,
    playPreviousChapter,
    canPlayNextChapter,
    canPlayPreviousChapter,
    jumpToTrack,
    seek,
    toggleRepeat,
    settings,
    totalTracks,
    currentIndex,
    queue,
    currentTime,
    duration,
    speed,
    setSpeed
  } = useListenSession('stream');

  const { sleepMinutes, remainingSeconds, startTimer, cancelTimer } = useSleepTimer(stopPlayback);
  const [showSleepOptions, setShowSleepOptions] = useState(false);

  const [queueOpen, setQueueOpen] = useState(false);
  const [lyricsExpanded, setLyricsExpanded] = useState(false);
  const [showSettings, setShowSettings] = useState(!hasQueue);
  const isMobileLayout = useMediaQuery(MOBILE_PLAYER_QUERY);

  const settingsPanelRef = useRef(null);
  const dragStartY = useRef(null);

  const handleSheetPointerDown = useCallback((e) => {
    dragStartY.current = e.clientY;
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  const handleSheetPointerMove = useCallback((e) => {
    if (dragStartY.current === null) return;
    const dy = Math.max(0, e.clientY - dragStartY.current);
    if (settingsPanelRef.current) {
      settingsPanelRef.current.style.transform = `translateY(${dy}px)`;
      settingsPanelRef.current.style.transition = 'none';
    }
  }, []);

  const handleSheetPointerUp = useCallback((e) => {
    if (dragStartY.current === null) return;
    const dy = e.clientY - dragStartY.current;
    dragStartY.current = null;
    if (settingsPanelRef.current) {
      settingsPanelRef.current.style.transform = '';
      settingsPanelRef.current.style.transition = '';
    }
    if (dy > 80) setShowSettings(false);
  }, []);

  const modeLabel = PLAYBACK_MODES.find((m) => m.id === localSettings.mode)?.label || 'Verse';
  const langLabel = languageLabel(localSettings.language);

  const upNext = useMemo(() => {
    if (!hasQueue) return [];
    return queue.slice(currentIndex + 1, currentIndex + 30);
  }, [queue, currentIndex, hasQueue]);

  const progressPct = duration > 0 ? (currentTime / duration) * 100 : 0;
  const isDragging = useRef(false);

  const getSeekRatio = (e, el) => {
    const rect = el.getBoundingClientRect();
    return Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    seek(getSeekRatio(e, e.currentTarget) * (duration || 0));
  };

  const handleProgressPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    isDragging.current = true;
    seek(getSeekRatio(e, e.currentTarget) * (duration || 0));
  };

  const handleProgressPointerMove = (e) => {
    if (!isDragging.current) return;
    seek(getSeekRatio(e, e.currentTarget) * (duration || 0));
  };

  const handleProgressPointerUp = () => {
    isDragging.current = false;
  };

  const handleProgressKeyDown = (e) => {
    if (e.key === 'ArrowRight') seek(Math.min(currentTime + 5, duration));
    if (e.key === 'ArrowLeft') seek(Math.max(currentTime - 5, 0));
  };

  return (
    <div className="spotify-shell spotify-shell--fit">
      <PlayerMobileNav
        active="stream"
        showSettings={showSettings}
        onToggleSettings={() => setShowSettings((v) => !v)}
      />

      <PlayerSidebar active="stream">
        <div className="spotify-sidebar-playlist">
          <p className="text-xs uppercase tracking-widest text-white/40 mb-2">Session</p>
          <p className="text-sm text-white/70">
            Ch {localSettings.startChapter}–{localSettings.endChapter} · {modeLabel}
          </p>
          {hasQueue && (
            <p className="text-xs text-gita-gold mt-1">
              Track {currentIndex + 1} of {totalTracks}
            </p>
          )}
        </div>
      </PlayerSidebar>

      <div className={`spotify-main-wrap spotify-main-wrap--fit ${hasQueue ? 'with-player' : ''} ${showSettings ? 'spotify-main-wrap--settings-open' : ''}`}>
        <main className="spotify-stage spotify-stage--apple">
          <div className="spotify-stage-header">
            <div className="spotify-stage-brand">
              <GitaLogo size={28} theme="dark" className="spotify-stage-logo shrink-0" />
              <div>
                <h1 className="spotify-stage-title">Now Playing</h1>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowSettings((v) => !v)}
              className={`spotify-stage-settings-btn ${showSettings ? 'active' : ''}`}
            >
              <Settings2 className="w-4 h-4" />
              Settings
            </button>
          </div>

          <div className="spotify-stage-panel">
            {!hasQueue ? (
              <div className="spotify-setup-prompt">
                <h2 className="text-2xl font-bold mb-2">Sacred Journey</h2>
                <p className="text-white/60 text-sm mb-4">
                  Configure your session in Settings, then press Start listening.
                </p>
                <button type="button" onClick={() => setShowSettings(true)} className="spotify-btn-primary">
                  Open Settings
                </button>
              </div>
            ) : (
              <ListenVerseStage
                streamWideMeaning
                track={currentTrack}
                artPaneClassName="spotify-stage-art-pane"
              >
                <div className="spotify-now-playing-bar">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">
                      Chapter {currentTrack.chapterNumber} · Verse {currentTrack.slokaNumber}
                    </p>
                    <p className="text-xs text-white/50">
                      {currentTrack.type === 'meaning' ? 'Meaning' : 'Recitation'} · {langLabel} ·{' '}
                      {currentIndex + 1} / {totalTracks}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="spotify-text-btn text-xs shrink-0"
                    onClick={() => setLyricsExpanded(true)}
                  >
                    Full screen
                  </button>
                </div>
                <div className="listen-lyrics-slot">
                  <LiveLyricsPanel
                    track={currentTrack}
                    currentTime={currentTime}
                    duration={duration}
                    seek={seek}
                    language={localSettings.language}
                    isPlaying={isPlaying}
                    layout={isMobileLayout ? 'stream' : 'stream-wide'}
                    fitScreen
                    useChapterBackdrop={false}
                    showWordBreakdown
                  />
                </div>
              </ListenVerseStage>
            )}
          </div>
        </main>

        {showSettings && (
          <>
            <button
              type="button"
              className="listen-settings-scrim lg:hidden"
              aria-label="Close settings"
              onClick={() => setShowSettings(false)}
            />
            <aside className="spotify-settings-panel" ref={settingsPanelRef}>
              <div
                className="sheet-drag-handle sheet-drag-handle--dark"
                aria-hidden="true"
                onPointerDown={handleSheetPointerDown}
                onPointerMove={handleSheetPointerMove}
                onPointerUp={handleSheetPointerUp}
                style={{ cursor: 'grab', touchAction: 'none' }}
              />
              {!hasQueue && (
                <div className="spotify-setup-hero">
                  <h2 className="text-2xl font-bold mb-1">Sacred Journey</h2>
                  <p className="text-white/60 text-sm">
                    Chapters {localSettings.startChapter}–{localSettings.endChapter} · {langLabel}
                  </p>
                  <button
                    type="button"
                    onClick={handleStart}
                    disabled={isLoading}
                    className="spotify-play-fab mt-4"
                  >
                    {isLoading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Play className="w-6 h-6 ml-0.5" />}
                  </button>
                </div>
              )}
              <ListenSettingsForm
                localSettings={localSettings}
                updateLocal={updateLocal}
                voice={voice}
                setVoice={setVoice}
                handleStart={handleStart}
                stopPlayback={stopPlayback}
                hasQueue={hasQueue}
                isLoading={isLoading}
                error={error}
                speed={speed}
                setSpeed={setSpeed}
                variant="dark"
              />
            </aside>
          </>
        )}

        {hasQueue && queueOpen && (
          <>
            <button
              type="button"
              className="listen-settings-scrim"
              aria-label="Close queue"
              onClick={() => setQueueOpen(false)}
            />
            <aside className="spotify-queue-panel spotify-queue-panel--sheet">
              <div className="spotify-queue-header">
                <h3>Up next</h3>
                <button type="button" onClick={() => setQueueOpen(false)} aria-label="Close queue">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <ul className="spotify-queue-list">
                {upNext.length === 0 ? (
                  <li className="text-sm text-white/50 px-4 py-6 text-center">End of session</li>
                ) : (
                  upNext.map((track, i) => (
                    <li key={track.id}>
                      <button
                        type="button"
                        onClick={() => {
                          jumpToTrack(currentIndex + 1 + i);
                          setQueueOpen(false);
                        }}
                        className="spotify-queue-item"
                      >
                        <SlokaArtImage
                          chapterNumber={track.chapterNumber}
                          slokaNumber={track.slokaNumber}
                          alt=""
                          className="w-10 h-10 rounded object-cover object-top shrink-0"
                        />
                        <div className="min-w-0">
                          <p className="truncate text-sm">Ch {track.chapterNumber} · V {track.slokaNumber}</p>
                          <p className="truncate text-xs text-white/50 capitalize">{track.type}</p>
                        </div>
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </aside>
          </>
        )}
      </div>

      {hasQueue && lyricsExpanded && currentTrack && (
        <div className="live-lyrics-overlay live-lyrics-overlay--fit">
          <div className="live-lyrics-overlay-header">
            <div>
              <p className="text-xs text-white/50 uppercase tracking-widest">Now playing</p>
              <p className="text-sm font-medium">{currentTrack.label}</p>
            </div>
            <button type="button" onClick={() => setLyricsExpanded(false)} className="spotify-icon-btn" aria-label="Close">
              <X className="w-6 h-6" />
            </button>
          </div>
          <LiveLyricsPanel
            track={currentTrack}
            currentTime={currentTime}
            duration={duration}
            seek={seek}
            language={localSettings.language}
            isPlaying={isPlaying}
            layout="fullscreen"
            showHeader={false}
            fitScreen
            showWordBreakdown
          />
        </div>
      )}

      {hasQueue && (
        <footer className="spotify-player-bar">
          <div
            className="spotify-bar-progress"
            role="slider"
            aria-label="Track progress"
            aria-valuenow={Math.round(progressPct)}
            aria-valuemin={0}
            aria-valuemax={100}
            tabIndex={0}
            onPointerDown={handleProgressPointerDown}
            onPointerMove={handleProgressPointerMove}
            onPointerUp={handleProgressPointerUp}
            onKeyDown={handleProgressKeyDown}
          >
            <div className="spotify-bar-progress-fill" style={{ width: `${progressPct}%` }} />
            <div className="spotify-bar-progress-thumb" style={{ left: `${progressPct}%` }} />
          </div>
          <div className="spotify-bar-inner">
            <div className="spotify-bar-left">
              <SlokaArtImage
                chapterNumber={currentTrack.chapterNumber}
                slokaNumber={currentTrack.slokaNumber}
                alt=""
                className="spotify-bar-art"
              />
              <div className="min-w-0 hidden sm:block">
                <p className="text-sm font-medium truncate">{currentTrack.label}</p>
                <p className="text-xs text-white/50">
                  Chapter {currentTrack.chapterNumber}{currentTrack.speaker ? ` · ${currentTrack.speaker}` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setLyricsExpanded((v) => !v)}
                className={`spotify-icon-btn spotify-bar-lyrics-btn ${lyricsExpanded ? 'active' : ''}`}
                aria-label="Toggle lyrics"
              >
                <Mic2 className="w-5 h-5" />
              </button>
            </div>
            <div className="spotify-bar-center">
              <div className="spotify-bar-controls">
                <button type="button" onClick={toggleRepeat} className={`spotify-icon-btn ${settings.repeat === 'all' ? 'active' : ''}`} aria-label="Loop">
                  <Repeat className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={playPreviousChapter}
                  className="spotify-icon-btn"
                  aria-label="Previous chapter"
                  disabled={!canPlayPreviousChapter}
                >
                  <ChevronsLeft className="w-5 h-5" />
                </button>
                <button type="button" onClick={playPrevious} className="spotify-icon-btn" aria-label="Previous track">
                  <SkipBack className="w-5 h-5" />
                </button>
                <button type="button" onClick={togglePlay} disabled={isLoading} className="spotify-bar-play" aria-label={isPlaying ? 'Pause' : 'Play'}>
                  {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                </button>
                <button type="button" onClick={playNext} className="spotify-icon-btn" aria-label="Next track">
                  <SkipForward className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={playNextChapter}
                  className="spotify-icon-btn"
                  aria-label="Next chapter"
                  disabled={!canPlayNextChapter}
                >
                  <ChevronsRight className="w-5 h-5" />
                </button>
              </div>
              <div className="spotify-bar-time">
                <span>{formatTime(currentTime)}</span>
                <div className="spotify-bar-time-track hidden md:block" onClick={handleSeek}>
                  <div className="spotify-bar-time-fill" style={{ width: `${progressPct}%` }} />
                </div>
                <span>{formatTime(duration)}</span>
              </div>
            </div>
            <div className="spotify-bar-right flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setQueueOpen(true)}
                className={`spotify-icon-btn ${queueOpen ? 'active' : ''}`}
                aria-label="Up next"
              >
                <ListMusic className="w-5 h-5" />
              </button>
              {sleepMinutes ? (
                <button
                  type="button"
                  onClick={cancelTimer}
                  className="spotify-icon-btn active flex items-center gap-1 text-xs"
                  title="Cancel sleep timer"
                >
                  <Moon className="w-4 h-4" />
                  <span className="hidden sm:inline">{formatSleepTime(remainingSeconds)}</span>
                </button>
              ) : showSleepOptions ? (
                <div className="flex items-center gap-1">
                  {[15, 30, 60].map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => { startTimer(m); setShowSleepOptions(false); }}
                      className="spotify-icon-btn text-xs px-2"
                    >
                      {m}m
                    </button>
                  ))}
                  <button type="button" onClick={() => setShowSleepOptions(false)} className="spotify-icon-btn">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowSleepOptions(true)}
                  className="spotify-icon-btn"
                  title="Sleep timer"
                >
                  <Moon className="w-4 h-4" />
                </button>
              )}
              <span className="text-xs text-white/50 hidden lg:inline">{currentIndex + 1} / {totalTracks}</span>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};

export default ListenStream;
